import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Wallet, TrendingUp, ArrowDownToLine, Clock, Loader2, Upload, FileText, Coins, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  pending: 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]',
  completed: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]',
  rejected: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]',
};
const STATUS_LABELS = { pending: 'Oczekuje', completed: 'Wyplacono', rejected: 'Odrzucono' };

export default function PayoutsPage() {
  const [tab, setTab] = useState('wallet');
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [wForm, setWForm] = useState({ amount_zorki: '', currency: 'PLN', payment_method: 'przelew', bank_details: '' });
  const [saving, setSaving] = useState(false);

  // Import
  const [importReport, setImportReport] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchWallet = useCallback(async () => {
    try { const r = await api.get('/wallet/me'); setWallet(r.data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  const handleWithdraw = async () => {
    setSaving(true);
    try {
      await api.post('/wallet/withdraw', wForm);
      toast.success('Zlecenie wyplaty zlozone');
      setWithdrawOpen(false);
      setWForm({ amount_zorki: '', currency: 'PLN', payment_method: 'przelew', bank_details: '' });
      fetchWallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/affiliate/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportReport(r.data);
      toast.success(`Import: ${r.data.imported} nowych, ${r.data.updated} zaktualizowanych`);
      fetchWallet();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad importu'); }
    finally { setImporting(false); e.target.value = ''; }
  };

  const exchangeRate = wallet?.exchange_rates?.[wForm.currency] || 1;
  const amountCurrency = ((parseFloat(wForm.amount_zorki) || 0) * exchangeRate).toFixed(2);

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}</div></div>;

  return (
    <div className="space-y-6" data-testid="payouts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Portfel Zorkow</h1>
          <p className="text-sm text-muted-foreground mt-1">Twoje zarobki i wyplaty. 1 Zorek = 1 PLN</p>
        </div>
        <Button onClick={() => setWithdrawOpen(true)} disabled={(wallet?.balance_zorki || 0) <= 0} data-testid="withdraw-button">
          <ArrowDownToLine className="h-4 w-4 mr-2" />Wyplac Zorki
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Saldo</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1 text-primary" data-testid="wallet-balance">{(wallet?.balance_zorki || 0).toFixed(1)} <span className="text-sm">ZRK</span></p></div>
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Coins className="h-5 w-5 text-primary" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Zarobione</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1 text-[hsl(var(--success))]">{(wallet?.total_earned || 0).toFixed(1)} ZRK</p></div>
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--success))]/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-[hsl(var(--success))]" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Wyplacone</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1">{(wallet?.total_withdrawn || 0).toFixed(1)} ZRK</p></div>
              <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center"><ArrowDownToLine className="h-5 w-5 text-muted-foreground" /></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50 bg-card/80">
          <CardContent className="pt-5 pb-4 px-5">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground uppercase tracking-wide">Kontakty wgrane</p><p className="text-2xl font-semibold font-heading tabular-nums mt-1">{wallet?.contacts_uploaded || 0}</p></div>
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--info))]/10 flex items-center justify-center"><Upload className="h-5 w-5 text-[hsl(var(--info))]" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="wallet"><Coins className="h-3.5 w-3.5 mr-1.5" />Portfel</TabsTrigger>
          <TabsTrigger value="import"><Upload className="h-3.5 w-3.5 mr-1.5" />Wgraj baze</TabsTrigger>
          <TabsTrigger value="withdrawals"><Receipt className="h-3.5 w-3.5 mr-1.5" />Wyplaty</TabsTrigger>
        </TabsList>

        {/* Wallet transactions */}
        <TabsContent value="wallet" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Historia transakcji</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Data</TableHead><TableHead>Opis</TableHead><TableHead>Kwota</TableHead></TableRow></TableHeader>
                <TableBody>
                  {!wallet?.transactions?.length ? (
                    <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">Brak transakcji. Wgraj baze kontaktow aby zaczac zarabiac!</TableCell></TableRow>
                  ) : wallet.transactions.map(t => (
                    <TableRow key={t.id} className="hover:bg-muted/30">
                      <TableCell className="text-xs font-mono text-muted-foreground">{t.created_at?.slice(0, 16).replace('T',' ')}</TableCell>
                      <TableCell className="text-sm">{t.description}</TableCell>
                      <TableCell className={`font-mono text-sm font-medium ${t.amount > 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'}`}>
                        {t.amount > 0 ? '+' : ''}{t.amount?.toFixed(1)} ZRK
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-border/50 bg-card/80 mt-4">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Kursy wymiany Zorkow</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {Object.entries(wallet?.exchange_rates || {}).map(([cur, rate]) => (
                  <div key={cur} className="p-3 rounded-lg bg-muted/30 border border-border/30 text-center">
                    <p className="text-xs text-muted-foreground">1 ZRK =</p>
                    <p className="text-lg font-semibold font-heading">{rate} {cur}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Smart Import */}
        <TabsContent value="import" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Wgraj baze kontaktow</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Wgraj plik CSV lub XLSX. System automatycznie:</p>
              <ul className="text-xs text-muted-foreground space-y-1 ml-4">
                <li>- Sprawdzi duplikaty po emailu i telefonie</li>
                <li>- Uzupelni brakujace dane istniejacych kontaktow</li>
                <li>- Sprawdzi czarna liste (pominie zablokowane)</li>
                <li>- Naliczy Ci <strong className="text-primary">0.1 Zorka za kazdy nowy kontakt</strong></li>
              </ul>
              <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
                <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-3">Przeciagnij plik lub kliknij aby wybrac</p>
                <Input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} className="max-w-xs mx-auto" data-testid="affiliate-import-file" disabled={importing} />
                {importing && <div className="flex items-center justify-center gap-2 mt-3"><Loader2 className="h-4 w-4 animate-spin" /><span className="text-sm">Importowanie i sprawdzanie...</span></div>}
              </div>
              {importReport && (
                <div className="space-y-3">
                  <Separator />
                  <h3 className="font-heading text-sm font-medium">Raport importu</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <div className="p-3 rounded-lg bg-[hsl(var(--success))]/5 border border-[hsl(var(--success))]/20 text-center">
                      <p className="text-lg font-semibold text-[hsl(var(--success))]">{importReport.imported}</p><p className="text-[10px] text-muted-foreground">Nowych</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[hsl(var(--info))]/5 border border-[hsl(var(--info))]/20 text-center">
                      <p className="text-lg font-semibold text-[hsl(var(--info))]">{importReport.updated}</p><p className="text-[10px] text-muted-foreground">Zaktualizowanych</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30 border border-border/30 text-center">
                      <p className="text-lg font-semibold text-muted-foreground">{importReport.duplicates}</p><p className="text-[10px] text-muted-foreground">Duplikatow</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[hsl(var(--destructive))]/5 border border-[hsl(var(--destructive))]/20 text-center">
                      <p className="text-lg font-semibold text-[hsl(var(--destructive))]">{importReport.blacklisted}</p><p className="text-[10px] text-muted-foreground">Czarna lista</p>
                    </div>
                    <div className="p-3 rounded-lg bg-[hsl(var(--warning))]/5 border border-[hsl(var(--warning))]/20 text-center">
                      <p className="text-lg font-semibold text-[hsl(var(--warning))]">{importReport.errors}</p><p className="text-[10px] text-muted-foreground">Bledow</p>
                    </div>
                  </div>
                  {importReport.zorki_earned > 0 && (
                    <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-center">
                      <p className="text-sm">Zarobiles: <span className="font-semibold text-primary">{importReport.zorki_earned?.toFixed(1)} Zorkow</span> za nowe kontakty!</p>
                    </div>
                  )}
                  {importReport.details?.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {importReport.details.slice(0, 30).map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-muted/30">
                          <Badge variant="outline" className={`text-[10px] w-20 justify-center ${d.status === 'blacklisted' ? 'text-destructive' : d.status === 'updated' ? 'text-[hsl(var(--info))]' : 'text-muted-foreground'}`}>{d.status}</Badge>
                          <span className="font-mono text-muted-foreground">{d.row}</span>
                          <span className="text-muted-foreground">{d.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Withdrawals */}
        <TabsContent value="withdrawals" className="mt-4">
          <Card className="border-border/50 bg-card/80">
            <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Historia wyplat</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Nr faktury</TableHead><TableHead>Kwota ZRK</TableHead><TableHead>Kwota wyplaty</TableHead><TableHead>Metoda</TableHead><TableHead>Status</TableHead><TableHead>Data</TableHead></TableRow></TableHeader>
                <TableBody>
                  {!wallet?.withdrawals?.length ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Brak wyplat</TableCell></TableRow>
                  ) : wallet.withdrawals.map(w => (
                    <TableRow key={w.id} className="hover:bg-muted/30">
                      <TableCell className="font-mono text-xs">{w.invoice_number}</TableCell>
                      <TableCell className="font-mono text-sm">{w.amount_zorki?.toFixed(1)} ZRK</TableCell>
                      <TableCell className="font-mono text-sm font-medium">{w.amount_currency?.toFixed(2)} {w.currency}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{w.payment_method}</TableCell>
                      <TableCell><Badge variant="outline" className={STATUS_STYLES[w.status] || ''}>{STATUS_LABELS[w.status] || w.status}</Badge></TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">{w.created_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Withdraw Dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Wyplata Zorkow</DialogTitle><DialogDescription>Wymien Zorki na walute i zlec wyplate</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-muted/30 border border-border/30 flex justify-between">
              <span className="text-sm text-muted-foreground">Dostepne:</span>
              <span className="font-semibold font-mono">{((wallet?.balance_zorki || 0) - (wallet?.pending_withdrawal || 0)).toFixed(1)} ZRK</span>
            </div>
            <div className="space-y-2">
              <Label>Kwota Zorkow</Label>
              <Input type="number" value={wForm.amount_zorki} onChange={e => setWForm({...wForm, amount_zorki: e.target.value})} placeholder="0" data-testid="withdraw-amount" />
            </div>
            <div className="space-y-2">
              <Label>Waluta wyplaty</Label>
              <Select value={wForm.currency} onValueChange={v => setWForm({...wForm, currency: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PLN">PLN (zloty)</SelectItem>
                  <SelectItem value="USD">USD (dolar)</SelectItem>
                  <SelectItem value="EUR">EUR (euro)</SelectItem>
                  <SelectItem value="USDT">USDT (Tether)</SelectItem>
                  <SelectItem value="USDC">USDC (Circle)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-center">
              <p className="text-xs text-muted-foreground">Do wyplaty:</p>
              <p className="text-xl font-semibold font-heading text-primary">{amountCurrency} {wForm.currency}</p>
              <p className="text-[10px] text-muted-foreground">Kurs: 1 ZRK = {exchangeRate} {wForm.currency}</p>
            </div>
            <div className="space-y-2">
              <Label>Dane do przelewu / adres portfela</Label>
              <Input value={wForm.bank_details} onChange={e => setWForm({...wForm, bank_details: e.target.value})} placeholder="IBAN / adres USDT TRC20..." data-testid="withdraw-bank" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setWithdrawOpen(false)}>Anuluj</Button>
            <Button onClick={handleWithdraw} disabled={saving || !wForm.amount_zorki || parseFloat(wForm.amount_zorki) <= 0} data-testid="withdraw-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Zlec wyplate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
